/**
 * TouchOSC .tosc parser — BROWSER port.
 *
 * Walks a TouchOSC v5 XML tree (the file extension `.tosc` is zlib-
 * compressed XML; .xml input is also supported). Emits a flat list of
 * records, one per <node>, each carrying its parent chain, frame,
 * color, message bindings (MIDI + OSC), and embedded Lua script body.
 *
 * ─── Why this file exists (2026-07-27) ───────────────────────────────
 * This is a port of `packages/bridge/src/tosc/parse.ts`, moved into the
 * PWA because the importer was DEAD: `ToscImportPreview` round-tripped
 * `bridge.toscParse()` through the legacy Node bridge, which is not
 * running and is slated for deletion, so picking a file failed with
 * "not connected" every time (and the error was invisible — see the
 * settings-toast fix landed alongside this).
 *
 * Parsing a .tosc is a pure file transform: inflate, parse XML, walk.
 * It never needed a daemon. Doing it in the browser kills the legacy
 * dependency outright rather than relocating it to OPBridge.
 *
 * Only the inflate step was Node-specific. Everything below the
 * "XML → tree" divider is byte-identical to the bridge original.
 *
 * Browser support: `DecompressionStream` is Chrome 80+, Firefox 113+,
 * Safari 16.4+ (March 2023). iPad Safari below that gets a clear error
 * rather than a silent failure.
 */

import { XMLParser } from 'fast-xml-parser';

// ───── Public types ────────────────────────────────────────────────

export type ToscColor = { r: string; g: string; b: string; a: string };
export type ToscFrame = { x: string; y: string; w: string; h: string };

export interface ToscMidiInfo {
  summary: string;
  type: string;
  channel: string;
  data1: string;
  data2: string;
  connections: string;
  send: boolean;
  receive: boolean;
}

export interface ToscOscInfo {
  summary: string;
  path: string;
  args: string;
  boilerplate: boolean;
}

export type ToscMessage =
  | { kind: 'midi'; info: ToscMidiInfo }
  | { kind: 'osc'; info: ToscOscInfo }
  | { kind: 'unhandled'; tag: string; info: { summary: string } };

export interface ToscRecord {
  id: string | undefined;
  type: string;
  name: string;
  text: string;
  frame: ToscFrame | undefined;
  color: ToscColor | undefined;
  parents: string[];
  messages: ToscMessage[];
  script: string;
}

// ───── XML loading ─────────────────────────────────────────────────

/** Browser replacement for Node's `inflateSync`. `.tosc` is zlib-wrapped
 *  deflate (the 0x78 CMF marker `looksZlib` checks for), which maps to
 *  DecompressionStream's 'deflate' format. We fall back to 'deflate-raw'
 *  because a headerless variant would otherwise throw an opaque
 *  "unexpected end of file" that tells the user nothing. */
async function inflate(bytes: Uint8Array): Promise<Uint8Array> {
  if (typeof DecompressionStream === 'undefined') {
    throw new Error(
      'This browser cannot decompress .tosc files (needs DecompressionStream: ' +
      'Chrome 80+, Firefox 113+, Safari 16.4+). Try a newer browser.',
    );
  }
  const run = async (format: 'deflate' | 'deflate-raw'): Promise<Uint8Array> => {
    const stream = new Blob([bytes as BlobPart]).stream()
      .pipeThrough(new DecompressionStream(format));
    return new Uint8Array(await new Response(stream).arrayBuffer());
  };
  try {
    return await run('deflate');
  } catch {
    try {
      return await run('deflate-raw');
    } catch {
      throw new Error('Not a valid .tosc file (zlib inflate failed on both deflate and deflate-raw).');
    }
  }
}

/** Inflate a .tosc buffer to its underlying XML string; pass-through
 *  for already-decompressed XML. Async only because the browser's
 *  decompression API is stream-based; the Node original was sync. */
export async function loadXml(
  bytes: Uint8Array,
  opts: { compressed?: boolean } = {},
): Promise<string> {
  const decoder = new TextDecoder('utf-8');
  if (opts.compressed ?? looksZlib(bytes)) {
    return decoder.decode(await inflate(bytes));
  }
  return decoder.decode(bytes);
}

/** zlib stream marker: first byte is 0x78 (CMF), the common second
 *  bytes are 0x01, 0x5e, 0x9c, 0xda (FLEVEL × FCHECK). */
function looksZlib(buf: Uint8Array): boolean {
  return buf.length >= 2 && buf[0] === 0x78;
}

// ───── XML → tree ──────────────────────────────────────────────────

/** Loose shape — fast-xml-parser returns nested objects; we walk
 *  defensively without committing to a strict interface for every
 *  TouchOSC variant. */
type XmlNode = Record<string, unknown> & { ':@'?: Record<string, string> };

const PARSER = new XMLParser({
  ignoreAttributes: false,
  attributeNamePrefix: '',
  attributesGroupName: ':@',
  preserveOrder: true,
  parseTagValue: false,
  parseAttributeValue: false,
  trimValues: false,
});

interface OrderedEl {
  ':@'?: Record<string, string>;
  [tag: string]: unknown;
}

/** fast-xml-parser preserveOrder mode returns arrays of single-key
 *  objects. This helper finds the first child element matching `tag`
 *  and returns its child array, or undefined. */
function findChild(node: OrderedEl[] | undefined, tag: string): OrderedEl[] | undefined {
  if (!node) return undefined;
  for (const el of node) {
    const keys = Object.keys(el).filter((k) => k !== ':@');
    if (keys[0] === tag) return el[tag] as OrderedEl[] | undefined;
  }
  return undefined;
}

/** All children matching `tag`, in document order. */
function findAllChildren(node: OrderedEl[] | undefined, tag: string): OrderedEl[] {
  if (!node) return [];
  const out: OrderedEl[] = [];
  for (const el of node) {
    const keys = Object.keys(el).filter((k) => k !== ':@');
    if (keys[0] === tag) out.push(el);
  }
  return out;
}

function attrs(el: OrderedEl): Record<string, string> {
  return el[':@'] ?? {};
}

function textOf(node: OrderedEl[] | undefined): string {
  if (!node) return '';
  for (const el of node) {
    const t = (el as { '#text'?: string })['#text'];
    if (typeof t === 'string') return t;
  }
  return '';
}

/** Single-step convenience: `<parent><tag>text</tag></parent>` → text. */
function findText(node: OrderedEl[] | undefined, tag: string, dflt = ''): string {
  const child = findChild(node, tag);
  if (!child) return dflt;
  return textOf(child) || dflt;
}

// ───── Property + value extraction ─────────────────────────────────

type PropDictValue = string | ToscColor | ToscFrame;

export function propDict(nodeChildren: OrderedEl[] | undefined): Record<string, PropDictValue> {
  const out: Record<string, PropDictValue> = {};
  const props = findChild(nodeChildren, 'properties');
  if (!props) return out;
  for (const propEl of findAllChildren(props, 'property')) {
    const propChildren = propEl['property'] as OrderedEl[] | undefined;
    const t = attrs(propEl)['type'] ?? '?';
    const keyChildren = findChild(propChildren, 'key');
    const valueChildren = findChild(propChildren, 'value');
    if (!keyChildren || !valueChildren) continue;
    const key = textOf(keyChildren).trim();
    if (!key) continue;
    if (t === 's' || t === 'i' || t === 'f' || t === 'b') {
      out[key] = textOf(valueChildren).trim();
    } else if (t === 'c') {
      out[key] = {
        r: findText(valueChildren, 'r'),
        g: findText(valueChildren, 'g'),
        b: findText(valueChildren, 'b'),
        a: findText(valueChildren, 'a'),
      };
    } else if (t === 'r') {
      out[key] = {
        x: findText(valueChildren, 'x'),
        y: findText(valueChildren, 'y'),
        w: findText(valueChildren, 'w'),
        h: findText(valueChildren, 'h'),
      };
    } else {
      // Unhandled prop type — keep a string echo for visibility.
      out[key] = textOf(valueChildren);
    }
  }
  return out;
}

/** LABEL / TEXT controls store their displayed text under
 *  <values>/<value>/<key>text</key><default>...</default>. */
export function valueText(nodeChildren: OrderedEl[] | undefined): string {
  const vals = findChild(nodeChildren, 'values');
  if (!vals) return '';
  for (const v of findAllChildren(vals, 'value')) {
    const vChildren = v['value'] as OrderedEl[] | undefined;
    if (findText(vChildren, 'key').trim() === 'text') {
      return findText(vChildren, 'default').trim();
    }
  }
  return '';
}

export function scriptExcerpt(nodeChildren: OrderedEl[] | undefined): string {
  const props = findChild(nodeChildren, 'properties');
  if (!props) return '';
  for (const propEl of findAllChildren(props, 'property')) {
    const propChildren = propEl['property'] as OrderedEl[] | undefined;
    if (findText(propChildren, 'key').trim() === 'script') {
      return findText(propChildren, 'value').trim();
    }
  }
  return '';
}

// ───── OSC path / argument expansion ───────────────────────────────

function expandPartial(p: OrderedEl): string {
  const partialChildren = p['partial'] as OrderedEl[] | undefined;
  const t = findText(partialChildren, 'type').trim();
  const val = findText(partialChildren, 'value').trim();
  const conv = findText(partialChildren, 'conversion').trim();
  if (t === 'CONSTANT') return val;
  if (t === 'PROPERTY') return `\${${val}}`;
  if (t === 'VALUE') return `<${val}:${conv.toLowerCase()}>`;
  if (t === 'INDEX') return `#${val}`;
  return `?${t}:${val}`;
}

function expandPath(pathEl: OrderedEl[] | undefined): string {
  if (!pathEl) return '';
  return findAllChildren(pathEl, 'partial').map(expandPartial).join('');
}

function expandArguments(argsEl: OrderedEl[] | undefined): string {
  if (!argsEl) return '';
  return findAllChildren(argsEl, 'partial').map(expandPartial).join(' ');
}

function connBits(b: string): string {
  if (!b) return '';
  const out: string[] = [];
  for (let i = 0; i < b.length; i++) {
    if (b[i] === '1') out.push(String(i + 1));
  }
  return out.join(',');
}

const BOILERPLATE_OSC_SUFFIXES = [
  '/${parent.name}/${name}/visible',
  '/${parent.name}/${name}/position/x',
  '/${parent.name}/${name}/position/y',
  '/${parent.name}/${name}/size/w',
  '/${parent.name}/${name}/size/h',
  '/${parent.name}/${name}/color',
];

export function isBoilerplateOsc(path: string): boolean {
  if (path === '/${parent.name}/${name}') return true;
  return BOILERPLATE_OSC_SUFFIXES.some((sfx) => path.endsWith(sfx));
}

function summarizeMidi(m: OrderedEl): ToscMidiInfo {
  const mChildren = m['midi'] as OrderedEl[] | undefined;
  const enabled = findText(mChildren, 'enabled', '1');
  const send = findText(mChildren, 'send', '0');
  const receive = findText(mChildren, 'receive', '0');
  const conn = connBits(findText(mChildren, 'connections'));
  const msg = findChild(mChildren, 'message');
  if (!msg) {
    return {
      summary: 'MIDI (no <message>)',
      type: '?', channel: '?', data1: '?', data2: '?',
      connections: conn, send: send === '1', receive: receive === '1',
    };
  }
  const mtype = findText(msg, 'type', '?');
  const ch = findText(msg, 'channel', '?');
  const d1 = findText(msg, 'data1', '?');
  const d2 = findText(msg, 'data2', '?');

  let valRule = '';
  const vals = findChild(mChildren, 'values');
  if (vals) {
    for (const v of findAllChildren(vals, 'value')) {
      const vChildren = v['value'] as OrderedEl[] | undefined;
      const vt = findText(vChildren, 'type');
      if (vt === 'VALUE') {
        const vk = findText(vChildren, 'key');
        const smin = findText(vChildren, 'scaleMin');
        const smax = findText(vChildren, 'scaleMax');
        valRule = `value=${vk}(${smin}..${smax})`;
        break;
      }
    }
  }

  const triggers: string[] = [];
  const triggersEl = findChild(mChildren, 'triggers');
  if (triggersEl) {
    for (const tr of findAllChildren(triggersEl, 'trigger')) {
      const trChildren = tr['trigger'] as OrderedEl[] | undefined;
      triggers.push(`${findText(trChildren, 'var')}=${findText(trChildren, 'condition')}`);
    }
  }

  const flags: string[] = [];
  if (send === '1') flags.push('send');
  if (receive === '1') flags.push('recv');
  if (!enabled || enabled === '0') flags.push('DISABLED');

  return {
    summary: `MIDI ${mtype} ch=${ch} d1=${d1} d2=${d2}  conn=${conn} [${flags.join(',')}] ${valRule} trig=${triggers.join('/')}`,
    type: mtype,
    channel: ch,
    data1: d1,
    data2: d2,
    connections: conn,
    send: send === '1',
    receive: receive === '1',
  };
}

function summarizeOsc(m: OrderedEl): ToscOscInfo {
  const mChildren = m['osc'] as OrderedEl[] | undefined;
  const path = expandPath(findChild(mChildren, 'path'));
  const args = expandArguments(findChild(mChildren, 'arguments'));
  const conn = connBits(findText(mChildren, 'connections'));
  const send = findText(mChildren, 'send', '0');
  const receive = findText(mChildren, 'receive', '0');
  const enabled = findText(mChildren, 'enabled', '1');
  const flags: string[] = [];
  if (send === '1') flags.push('send');
  if (receive === '1') flags.push('recv');
  if (enabled === '0') flags.push('DISABLED');
  return {
    summary: `OSC ${path}  args=[${args}]  conn=${conn} [${flags.join(',')}]`,
    path,
    args,
    boilerplate: isBoilerplateOsc(path),
  };
}

export function extractMessages(nodeChildren: OrderedEl[] | undefined): ToscMessage[] {
  const out: ToscMessage[] = [];
  const msgsEl = findChild(nodeChildren, 'messages');
  if (!msgsEl) return out;
  for (const child of msgsEl) {
    const keys = Object.keys(child).filter((k) => k !== ':@');
    const tag = keys[0];
    if (tag === 'midi') {
      out.push({ kind: 'midi', info: summarizeMidi(child) });
    } else if (tag === 'osc') {
      out.push({ kind: 'osc', info: summarizeOsc(child) });
    } else {
      const t = tag ?? '?';
      out.push({ kind: 'unhandled', tag: t, info: { summary: `${t.toUpperCase()} (unhandled)` } });
    }
  }
  return out;
}

// ───── Tree walk ───────────────────────────────────────────────────

export function walk(
  rootChildren: OrderedEl[] | undefined,
  parentChain: string[] = [],
  out: ToscRecord[] = [],
): ToscRecord[] {
  const childrenEl = findChild(rootChildren, 'children');
  if (!childrenEl) return out;
  for (const nodeEl of findAllChildren(childrenEl, 'node')) {
    const nodeChildren = nodeEl['node'] as OrderedEl[] | undefined;
    const t = attrs(nodeEl)['type'] ?? '';
    const p = propDict(nodeChildren);
    const name = (typeof p['name'] === 'string' ? p['name'] : undefined) ?? '?';
    const rec: ToscRecord = {
      id: attrs(nodeEl)['ID'],
      type: t,
      name,
      text: valueText(nodeChildren) || (typeof p['text'] === 'string' ? p['text'] : '') || '',
      frame: typeof p['frame'] === 'object' && p['frame'] && 'x' in p['frame']
        ? (p['frame'] as ToscFrame) : undefined,
      color: typeof p['color'] === 'object' && p['color'] && 'r' in p['color']
        ? (p['color'] as ToscColor) : undefined,
      parents: [...parentChain],
      messages: extractMessages(nodeChildren),
      script: scriptExcerpt(nodeChildren),
    };
    out.push(rec);
    walk(nodeChildren, [...parentChain, name], out);
  }
  return out;
}

/** Top-level entry: XML string → flat record list. The TouchOSC file's
 *  outermost element is <lexml> with one <node> child (the root
 *  group); we walk from that root group's children. */
export function parseToscXml(xml: string): ToscRecord[] {
  const parsed = PARSER.parse(xml) as OrderedEl[];
  // parsed is the array of top-level elements; the root is usually
  // <lexml>. The Python parser finds the first <node> under root.
  const lexml = findChild(parsed, 'lexml');
  const topNode = lexml ? findChild(lexml, 'node') : findChild(parsed, 'node');
  if (!topNode) return [];
  return walk(topNode);
}

/** Convenience: bytes (compressed or raw XML) → records. Async because
 *  the browser inflate is stream-based; `parseToscXml` itself stays sync. */
export async function parseTosc(bytes: Uint8Array): Promise<ToscRecord[]> {
  return parseToscXml(await loadXml(bytes));
}
